/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { CopyButton } from '../CopyButton';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type IngestionSourceRevealDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function IngestionSourceRevealDialog({ actionDialogReturn, onNavigate: _onNavigate }: IngestionSourceRevealDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    setActionDialog,
  } = actionDialogReturn;

  if (!actionDialog || actionDialog.kind !== 'ingestion-source-reveal') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };
  const isGrafana = actionDialog.sourceKind === 'grafana';

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <div className="dialogPanel" onClick={(e) => e.stopPropagation()}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Ingestion</div>
            <h3>&quot;{actionDialog.name}&quot; created — copy these now</h3>
            <p>The {isGrafana ? 'bearer token' : 'signing secret'} is only shown once. Store it somewhere safe.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Webhook URL</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                readOnly
                value={actionDialog.webhookUrl}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
                onFocus={(e) => e.target.select()}
              />
              <CopyButton value={actionDialog.webhookUrl} label="Copy webhook URL" />
            </div>
          </label>
          <label className="settingsField">
            <span>{isGrafana ? 'Bearer token' : 'Signing secret'}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                readOnly
                value={actionDialog.secret}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
                onFocus={(e) => e.target.select()}
              />
              <CopyButton value={actionDialog.secret} label="Copy secret" />
            </div>
            <p className="fieldHint" style={{ color: 'var(--amber)' }}>
              This {isGrafana ? 'token' : 'secret'} will not be shown again after closing this dialog.
            </p>
          </label>
          {isGrafana ? (
            <div className="keyRevealEnvHint">
              <span className="keyRevealEnvHintLabel">Set up in Grafana</span>
              <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: 'var(--text-soft)' }}>
                <li>In Grafana, go to <strong>Alerting → Contact points → New contact point</strong>.</li>
                <li>Choose integration type <strong>Webhook</strong> and paste the webhook URL above.</li>
                <li>
                  Add a custom header named <code>Authorization</code> with value{' '}
                  <code>Bearer {actionDialog.secret}</code>.
                </li>
                <li>Save the contact point and route the alert rules you want Cindro to see to it.</li>
              </ol>
              <div className="keyRevealEnvRow" style={{ marginTop: 10 }}>
                <code className="keyRevealEnvCode">Authorization: Bearer {actionDialog.secret}</code>
                <CopyButton value={`Authorization: Bearer ${actionDialog.secret}`} label="Copy header" />
              </div>
            </div>
          ) : (
            <div className="keyRevealEnvHint">
              <span className="keyRevealEnvHintLabel">Sign requests with this secret</span>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-soft)' }}>
                Sign each request body with HMAC-SHA256 using this secret and send the result as the{' '}
                <code>X-Cindro-Signature</code> header.
              </p>
            </div>
          )}
        </div>
        <div className="dialogActions">
          <button type="button" className="primaryButton" onClick={close}>Done</button>
        </div>
      </div>
    </div>
  );
}

export { IngestionSourceRevealDialog };
